import React, { useState, useRef, useCallback } from 'react';
import { ItemCard, SavedItem } from './ItemCard';
import { ThumbnailSize, ItemFeatures } from '../../types/viewComposition';
import '../../styles/components/swipeableItem.css';

export type SwipeAction = 'delete' | 'remove-from-collection' | 'add-to-collection' | 'set-quantity-zero' | 'decrease-quantity' | 'increase-quantity';

interface SwipeableItemCardProps {
  item: SavedItem;
  size: ThumbnailSize;
  features: ItemFeatures;
  isSelected: boolean;
  isSelectMode?: boolean;
  token?: string | null;
  showQuantityInName?: boolean;
  highlightZeroQuantity?: boolean;
  hideMenu?: boolean;
  isFadingOut?: boolean;
  isGuestToken?: boolean;
  currentUserId?: number;
  isSharedCollection?: boolean;
  isReadOnly?: boolean;
  showLockIcon?: boolean;
  isInBindle?: boolean;
  
  // Swipe configuration
  leftSwipeAction?: SwipeAction;
  rightSwipeAction?: SwipeAction;
  swipeEnabled?: boolean;
  useQuantityBadge?: boolean;
  
  // Callbacks from ItemCard
  onItemClick?: (item: any) => void;
  onImageClick?: (item: any) => void;
  onMenuClick?: (item: any) => void;
  onCheckboxToggle?: (item: any, e: React.MouseEvent) => void;
  onQuantityChange?: (itemId: number, newQuantity: number) => void;
  onPhotoUpload?: (itemId: number, file: File) => Promise<void>;
  onOpenImageSelection?: (itemId: number) => void;
  renderOverlay?: (item: any) => React.ReactNode;
  
  // Swipe action callbacks
  onSwipeAction?: (item: any, action: SwipeAction) => void;
  onQuantityBadgeClick?: (item: any) => void;
}

const ACTION_LABELS: Record<SwipeAction, string> = {
  'delete': 'Delete',
  'remove-from-collection': 'Remove',
  'add-to-collection': 'Add to...',
  'set-quantity-zero': 'Zero',
  'decrease-quantity': '−1',
  'increase-quantity': '+1',
};

const ACTION_COLORS: Record<SwipeAction, { bg: string; text: string }> = {
  'delete': { bg: 'var(--color-danger)', text: 'var(--color-text-inverse)' },
  'remove-from-collection': { bg: 'var(--color-warning)', text: 'var(--color-text-inverse)' },
  'add-to-collection': { bg: 'var(--color-accent-secondary)', text: 'var(--color-text-inverse)' },
  'set-quantity-zero': { bg: '#8b5cf6', text: 'var(--color-text-inverse)' },
  'decrease-quantity': { bg: 'var(--color-warning)', text: 'var(--color-text-inverse)' },
  'increase-quantity': { bg: 'var(--color-success)', text: 'var(--color-text-inverse)' },
};

const ACTION_ICONS: Record<SwipeAction, React.ReactNode> = {
  'delete': (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"></polyline>
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
    </svg>
  ),
  'remove-from-collection': (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
  ),
  'add-to-collection': (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
      <line x1="12" y1="11" x2="12" y2="17"></line>
      <line x1="9" y1="14" x2="15" y2="14"></line>
    </svg>
  ),
  'set-quantity-zero': (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"></circle>
      <text x="12" y="16" textAnchor="middle" fontSize="12" fill="currentColor" stroke="none">0</text>
    </svg>
  ),
  'decrease-quantity': (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
  ),
  'increase-quantity': (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"></line>
      <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
  ),
};

export const SwipeableItemCard: React.FC<SwipeableItemCardProps> = ({
  item,
  size,
  features,
  isSelected,
  isSelectMode,
  token,
  showQuantityInName,
  highlightZeroQuantity,
  hideMenu,
  isFadingOut,
  isGuestToken,
  currentUserId,
  isSharedCollection,
  isReadOnly,
  showLockIcon,
  leftSwipeAction,
  rightSwipeAction,
  swipeEnabled = true,
  useQuantityBadge,
  onItemClick,
  onImageClick,
  onMenuClick,
  onCheckboxToggle,
  onQuantityChange,
  onPhotoUpload,
  onOpenImageSelection,
  renderOverlay,
  onSwipeAction,
  onQuantityBadgeClick,
  isInBindle,
}) => {
  const [translateX, setTranslateX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const isHorizontalSwipe = useRef<boolean | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const SWIPE_THRESHOLD = 80; // Pixels to trigger action
  const MAX_SWIPE = 100; // Maximum swipe distance
  
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!swipeEnabled || isSelectMode) return;
    
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    isHorizontalSwipe.current = null;
    setIsDragging(true);
  }, [swipeEnabled, isSelectMode]);
  
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging || !swipeEnabled || isSelectMode) return;
    
    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const diffX = currentX - touchStartX.current;
    const diffY = currentY - touchStartY.current;
    
    // Determine swipe direction on first significant movement
    if (isHorizontalSwipe.current === null) {
      if (Math.abs(diffX) > 10 || Math.abs(diffY) > 10) {
        isHorizontalSwipe.current = Math.abs(diffX) > Math.abs(diffY);
      }
    }
    
    // Only handle horizontal swipes
    if (!isHorizontalSwipe.current) return;
    
    // Prevent vertical scroll during horizontal swipe
    e.preventDefault();
    
    // Check if swipe direction has a configured action
    const isLeftSwipe = diffX < 0;
    const isRightSwipe = diffX > 0;
    
    if (isLeftSwipe && !leftSwipeAction) return;
    if (isRightSwipe && !rightSwipeAction) return;
    
    // Apply resistance at edges
    let newTranslateX = diffX;
    if (Math.abs(newTranslateX) > MAX_SWIPE) {
      const overflow = Math.abs(newTranslateX) - MAX_SWIPE;
      newTranslateX = (newTranslateX > 0 ? 1 : -1) * (MAX_SWIPE + overflow * 0.2);
    }
    
    setTranslateX(newTranslateX);
  }, [isDragging, swipeEnabled, isSelectMode, leftSwipeAction, rightSwipeAction]);
  
  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;
    
    setIsDragging(false);
    isHorizontalSwipe.current = null;
    
    const isLeftSwipe = translateX < -SWIPE_THRESHOLD;
    const isRightSwipe = translateX > SWIPE_THRESHOLD;
    
    if (isLeftSwipe && leftSwipeAction && onSwipeAction) {
      onSwipeAction(item, leftSwipeAction);
    } else if (isRightSwipe && rightSwipeAction && onSwipeAction) {
      onSwipeAction(item, rightSwipeAction);
    }
    
    // Animate back to center
    setTranslateX(0);
  }, [isDragging, translateX, leftSwipeAction, rightSwipeAction, item, onSwipeAction]);
  
  // Calculate action reveal opacity based on swipe distance
  const leftActionOpacity = Math.min(1, Math.abs(Math.min(0, translateX)) / SWIPE_THRESHOLD);
  const rightActionOpacity = Math.min(1, Math.max(0, translateX) / SWIPE_THRESHOLD);
  
  const showLeftAction = translateX < -20 && leftSwipeAction;
  const showRightAction = translateX > 20 && rightSwipeAction;
  
  return (
    <div 
      ref={containerRef}
      className={`swipeable-item-container ${isDragging ? 'dragging' : ''}`}
      data-item-id={item.id}
    >
      {/* Left action background (revealed on left swipe) */}
      {leftSwipeAction && (
        <div 
          className="swipe-action-bg swipe-action-left"
          style={{ 
            backgroundColor: ACTION_COLORS[leftSwipeAction].bg,
            opacity: leftActionOpacity,
          }}
        >
          <div className="swipe-action-content" style={{ color: ACTION_COLORS[leftSwipeAction].text }}>
            {ACTION_ICONS[leftSwipeAction]}
            <span>{ACTION_LABELS[leftSwipeAction]}</span>
          </div>
        </div>
      )}
      
      {/* Right action background (revealed on right swipe) */}
      {rightSwipeAction && (
        <div 
          className="swipe-action-bg swipe-action-right"
          style={{ 
            backgroundColor: ACTION_COLORS[rightSwipeAction].bg,
            opacity: rightActionOpacity,
          }}
        >
          <div className="swipe-action-content" style={{ color: ACTION_COLORS[rightSwipeAction].text }}>
            {ACTION_ICONS[rightSwipeAction]}
            <span>{ACTION_LABELS[rightSwipeAction]}</span>
          </div>
        </div>
      )}
      
      {/* The actual card that moves */}
      <div 
        className="swipeable-item-card"
        style={{
          transform: `translateX(${translateX}px)`,
          transition: isDragging ? 'none' : 'transform 0.3s ease-out',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <ItemCard
          item={item}
          size={size}
          features={features}
          isSelected={isSelected}
          isSelectMode={isSelectMode}
          token={token}
          showQuantityInName={showQuantityInName}
          highlightZeroQuantity={highlightZeroQuantity}
          hideMenu={hideMenu}
          isFadingOut={isFadingOut}
          isGuestToken={isGuestToken}
          currentUserId={currentUserId}
          isSharedCollection={isSharedCollection}
          isReadOnly={isReadOnly}
          showLockIcon={showLockIcon}
          useQuantityBadge={useQuantityBadge}
          onItemClick={onItemClick}
          onImageClick={onImageClick}
          onMenuClick={onMenuClick}
          onCheckboxToggle={onCheckboxToggle}
          onQuantityChange={onQuantityChange}
          onPhotoUpload={onPhotoUpload}
          onQuantityBadgeClick={onQuantityBadgeClick}
          onOpenImageSelection={onOpenImageSelection}
          renderOverlay={renderOverlay}
          isInBindle={isInBindle}
        />
      </div>
    </div>
  );
};
